"use client";

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Copy,
  RefreshCw,
  Terminal,
  Globe,
  ShieldCheck,
  Zap,
  AlertTriangle,
  Code2,
  Trash2,
  Play,
  Save,
  Clock,
  User,
  Fingerprint,
  Check,
  Eye,
  EyeOff,
  KeyRound,
  Lock,
  Radio,
  CheckCircle2,
  FileCode2,
  ExternalLink,
  ShieldAlert,
  ChevronLeft,
  ChevronRight,
  Filter
} from "lucide-react";
import { cn } from "@/lib/utils";
import { RollApiKeyModal } from "@/components/modals/roll-api-key-modal";

export default function ApiPage() {
  const [isLive, setIsLive] = useState(true);
  const [showSecret, setShowSecret] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("https://api.merchant.com/v1/paytaara-webhook");
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [activeLang, setActiveLang] = useState<"node" | "php" | "python" | "curl">("node");
  const [isSavingWebhook, setIsSavingWebhook] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([
    "payment.success",
    "payout.processed",
    "virtual_account.created"
  ]);

  // Webhook Logs Pagination & Filter State
  const [webhookPage, setWebhookPage] = useState(1);
  const [webhookFilter, setWebhookFilter] = useState<"all" | "success" | "failed">("all");

  const allWebhookLogs = [
    { id: 1, event: "payment.success", status: "200 OK", response: '{"received": true}', retries: 0, time: "2 mins ago", type: "success" },
    { id: 2, event: "payout.processed", status: "504 TIMEOUT", response: "Gateway Timeout (504)", retries: 3, time: "14 mins ago", type: "error" },
    { id: 3, event: "virtual_account.created", status: "200 OK", response: '{"status": "assigned"}', retries: 0, time: "25 mins ago", type: "success" },
    { id: 4, event: "payment.success", status: "200 OK", response: '{"received": true}', retries: 0, time: "1 hour ago", type: "success" },
    { id: 5, event: "dispute.opened", status: "500 SERVER ERROR", response: "Internal Server Error 500", retries: 2, time: "2 hours ago", type: "error" },
    { id: 6, event: "payout.processed", status: "200 OK", response: '{"status": "settled"}', retries: 0, time: "3 hours ago", type: "success" },
    { id: 7, event: "payment.success", status: "200 OK", response: '{"received": true}', retries: 0, time: "4 hours ago", type: "success" },
    { id: 8, event: "virtual_account.created", status: "404 NOT FOUND", response: "Endpoint Not Found", retries: 1, time: "5 hours ago", type: "error" },
    { id: 9, event: "payment.success", status: "200 OK", response: '{"received": true}', retries: 0, time: "6 hours ago", type: "success" },
    { id: 10, event: "payout.processed", status: "200 OK", response: '{"status": "settled"}', retries: 0, time: "7 hours ago", type: "success" },
    { id: 11, event: "dispute.opened", status: "504 TIMEOUT", response: "Connection Timeout", retries: 3, time: "8 hours ago", type: "error" },
    { id: 12, event: "payment.success", status: "200 OK", response: '{"received": true}', retries: 0, time: "9 hours ago", type: "success" },
    { id: 13, event: "virtual_account.created", status: "200 OK", response: '{"status": "assigned"}', retries: 0, time: "10 hours ago", type: "success" },
    { id: 14, event: "payout.processed", status: "500 SERVER ERROR", response: "Database Deadlock", retries: 2, time: "11 hours ago", type: "error" },
    { id: 15, event: "payment.success", status: "200 OK", response: '{"received": true}', retries: 0, time: "12 hours ago", type: "success" },
  ];

  const filteredWebhookLogs = allWebhookLogs.filter(log => {
    if (webhookFilter === "success") return log.type === "success";
    if (webhookFilter === "failed") return log.type === "error";
    return true;
  });

  const ITEMS_PER_PAGE = 10;
  const totalPages = Math.ceil(filteredWebhookLogs.length / ITEMS_PER_PAGE);
  const paginatedWebhookLogs = filteredWebhookLogs.slice((webhookPage - 1) * ITEMS_PER_PAGE, webhookPage * ITEMS_PER_PAGE);

  const handleCopy = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(type);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleToggleEvent = (eventName: string) => {
    if (selectedEvents.includes(eventName)) {
      setSelectedEvents(selectedEvents.filter(e => e !== eventName));
    } else {
      setSelectedEvents([...selectedEvents, eventName]);
    }
  };

  const keys = isLive ? {
    public: "pk_live_8a7b6c5d4e3f2g1h0i9j8k7l6m5n4o3p2q1r0s9t",
    secret: "sk_live_99887766554433221100aabbccddeeff12345678",
    webhookSecret: "whsec_live_a1b2c3d4e5f67890123456789abcdef"
  } : {
    public: "pk_test_0x1y2z3a4b5c6d7e8f9g0h1i2j3k4l5m6n7o8p",
    secret: "sk_test_11223344556677889900aabbccddeeff87654321",
    webhookSecret: "whsec_test_9876543210fedcba9876543210fedcba"
  };

  const codeSnippets = {
    node: `const Paytaara = require('paytaara-node');
const paytaara = new Paytaara('${isLive ? keys.secret : keys.secret}');

// Initialize Virtual Account Charge
const account = await paytaara.virtualAccounts.create({
  merchant_id: "mch_98765",
  currency: "NGN",
  amount: 500000
});`,
    php: `use Paytaara\\PaytaaraClient;

$paytaara = new PaytaaraClient('${isLive ? keys.secret : keys.secret}');

$account = $paytaara->virtualAccounts->create([
    'merchant_id' => 'mch_98765',
    'currency' => 'NGN',
    'amount' => 500000
]);`,
    python: `import paytaara

paytaara.api_key = "${isLive ? keys.secret : keys.secret}"

account = paytaara.VirtualAccount.create(
    merchant_id="mch_98765",
    currency="NGN",
    amount=500000
)`,
    curl: `curl -X POST https://api.paytaara.com/v1/virtual-accounts \\
  -H "Authorization: Bearer ${isLive ? keys.secret : keys.secret}" \\
  -H "Content-Type: application/json" \\
  -d '{"merchant_id": "mch_98765", "currency": "NGN", "amount": 500000}'`
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      
      {/* Header & Mode Switcher */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-border pb-5">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">Developer Infrastructure</h1>
            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 text-[10px] font-bold uppercase tracking-wider">
              API v2.4 Operational
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Manage your API keys, webhook signing secrets, IP whitelists, and event triggers.
          </p>
        </div>

        {/* Environment Selector Banner */}
        <div className="flex items-center gap-3 bg-card border border-border p-2 rounded-xl shadow-sm">
          <div className="flex items-center gap-2 px-2">
            <Radio className={cn("h-4 w-4 animate-pulse", isLive ? "text-emerald-500" : "text-amber-500")} />
            <span className="text-xs font-semibold text-muted-foreground">Environment:</span>
          </div>
          <div className="flex items-center gap-2 bg-muted/50 p-1 rounded-lg border border-border/50">
            <button
              onClick={() => setIsLive(false)}
              className={cn(
                "px-3 py-1 text-xs font-bold rounded-md transition-all",
                !isLive ? "bg-amber-500 text-white shadow-sm" : "text-muted-foreground hover:text-foreground"
              )}
            >
              Test Sandbox
            </button>
            <button
              onClick={() => setIsLive(true)}
              className={cn(
                "px-3 py-1 text-xs font-bold rounded-md transition-all",
                isLive ? "bg-emerald-600 text-white shadow-sm" : "text-muted-foreground hover:text-foreground"
              )}
            >
              Live Production
            </button>
          </div>
        </div>
      </div>

      <Tabs defaultValue="keys" className="w-full space-y-6">
        <TabsList className="h-11 w-full justify-start gap-2 bg-muted/40 p-1 rounded-xl border border-border">
          <TabsTrigger value="keys" className="h-9 rounded-lg px-4 text-xs font-semibold data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-sm">
            <KeyRound className="h-3.5 w-3.5 mr-2" /> API Keys & Security
          </TabsTrigger>
          <TabsTrigger value="webhooks" className="h-9 rounded-lg px-4 text-xs font-semibold data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-sm">
            <Globe className="h-3.5 w-3.5 mr-2" /> Webhooks & Signatures
          </TabsTrigger>
          <TabsTrigger value="code" className="h-9 rounded-lg px-4 text-xs font-semibold data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-sm">
            <Code2 className="h-3.5 w-3.5 mr-2" /> SDK & Code Playground
          </TabsTrigger>
          <TabsTrigger value="logs" className="h-9 rounded-lg px-4 text-xs font-semibold data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-sm">
            <Clock className="h-3.5 w-3.5 mr-2" /> Request Audit Logs
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: API KEYS & SECURITY */}
        <TabsContent value="keys" className="space-y-6 m-0">
          <div className="grid lg:grid-cols-3 gap-6">
            
            {/* Left: Keys Manager */}
            <Card className="lg:col-span-2 border shadow-sm">
              <CardHeader className="py-4 px-5 border-b bg-muted/10 flex flex-row items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    <Terminal className="h-4 w-4" />
                  </div>
                  <div>
                    <CardTitle className="text-sm font-bold">
                      {isLive ? "Production API Keys" : "Sandbox Test Keys"}
                    </CardTitle>
                    <CardDescription className="text-[11px]">
                      Use these credentials to authenticate API calls to PayTaara.
                    </CardDescription>
                  </div>
                </div>
                <RollApiKeyModal>
                  <Button variant="outline" size="sm" className="h-8 text-xs font-semibold text-destructive border-destructive/20 hover:bg-destructive/10">
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Roll Secret Key
                  </Button>
                </RollApiKeyModal>
              </CardHeader>
              
              <CardContent className="p-5 space-y-6">
                
                {/* Public Key */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs font-bold text-foreground">
                      {isLive ? "Live" : "Test"} Public Key (Client-side / Frontend)
                    </Label>
                    <Badge variant="outline" className={cn(
                      "text-[10px] h-5 font-bold uppercase border",
                      isLive ? "border-emerald-500/30 text-emerald-600 bg-emerald-500/10" : "border-amber-500/30 text-amber-600 bg-amber-500/10"
                    )}>Active</Badge>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input readOnly value={keys.public} className="font-mono text-xs h-10 bg-muted/30 pr-10 border-border" />
                    </div>
                    <Button variant="outline" size="sm" className="h-10 px-3 font-semibold text-xs" onClick={() => handleCopy(keys.public, 'public')}>
                      {copiedKey === 'public' ? (
                        <>
                          <Check className="h-3.5 w-3.5 text-emerald-500 mr-1.5" /> Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">Used for public checkout widgets and client SDK initializations.</p>
                </div>

                {/* Secret Key */}
                <div className="space-y-2 pt-2 border-t">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs font-bold text-foreground flex items-center gap-1.5">
                      <Lock className="h-3.5 w-3.5 text-amber-500" />
                      {isLive ? "Live" : "Test"} Secret Key (Server-side Only)
                    </Label>
                    <span className="text-[10px] text-destructive font-bold uppercase tracking-wider flex items-center gap-1">
                      <ShieldAlert className="h-3 w-3" /> Never commit to repository
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input 
                        type={showSecret ? "text" : "password"} 
                        readOnly 
                        value={keys.secret} 
                        className="font-mono text-xs h-10 bg-muted/30 pr-10 border-border" 
                      />
                      <button 
                        type="button" 
                        onClick={() => setShowSecret(!showSecret)} 
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <Button variant="outline" size="sm" className="h-10 px-3 font-semibold text-xs" onClick={() => handleCopy(keys.secret, 'secret')}>
                      {copiedKey === 'secret' ? (
                        <>
                          <Check className="h-3.5 w-3.5 text-emerald-500 mr-1.5" /> Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">Keep this key strictly confidential. It can initiate transfers and settlements.</p>
                </div>

                {/* Warning Banner */}
                <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-start gap-3 text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <p className="text-xs leading-relaxed">
                    If you suspect your secret key has been exposed, roll it immediately. Rolling a key invalidates the previous key instantly.
                  </p>
                </div>

              </CardContent>
            </Card>

            {/* Right: IP Whitelisting & Origin Guard */}
            <Card className="border shadow-sm flex flex-col">
              <CardHeader className="py-4 px-5 border-b bg-muted/10">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-emerald-500" />
                  <CardTitle className="text-sm font-bold">IP Restrictions (Firewall)</CardTitle>
                </div>
                <CardDescription className="text-[11px]">
                  Only requests from these IP addresses can execute secret actions.
                </CardDescription>
              </CardHeader>
              
              <CardContent className="p-5 flex-1 space-y-4">
                <div className="flex gap-2">
                  <Input placeholder="e.g. 198.51.100.1" className="h-9 text-xs font-mono bg-background border-border" />
                  <Button size="sm" className="h-9 text-xs font-bold px-4">Whitelisting</Button>
                </div>

                <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1 no-scrollbar">
                  {[
                    { ip: "198.51.100.1", label: "Primary App Server" },
                    { ip: "192.168.1.42", label: "Dev Staging" },
                    { ip: "172.16.0.1", label: "Production Worker" }
                  ].map((entry, i) => (
                    <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/30 border border-border/60 hover:border-border transition-all">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-emerald-500" />
                        <div>
                          <p className="text-xs font-mono font-bold text-foreground">{entry.ip}</p>
                          <p className="text-[10px] text-muted-foreground">{entry.label}</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
              <div className="p-3.5 bg-muted/10 border-t flex justify-between items-center text-xs font-semibold text-muted-foreground">
                <span>Whitelisted Nodes: 3 / 25</span>
                <Badge variant="outline" className="text-[9px] uppercase font-bold text-emerald-600 border-emerald-500/20">Enforced</Badge>
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* TAB 2: WEBHOOKS & SIGNATURES */}
        <TabsContent value="webhooks" className="space-y-6 m-0">
          <div className="grid lg:grid-cols-3 gap-6">
            
            {/* Left: Webhook Destination & Signing Secret */}
            <Card className="lg:col-span-2 border shadow-sm">
              <CardHeader className="py-4 px-5 border-b bg-muted/10">
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-primary" />
                  <CardTitle className="text-sm font-bold">Webhook Destination & HMAC Signature</CardTitle>
                </div>
                <CardDescription className="text-[11px]">
                  PayTaara sends real-time HTTP POST notifications to your server when payment events happen.
                </CardDescription>
              </CardHeader>
              
              <CardContent className="p-5 space-y-6">
                
                {/* Endpoint URL */}
                <div className="space-y-2">
                  <Label className="text-xs font-bold text-foreground">Callback Endpoint URL</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={webhookUrl} 
                      onChange={(e) => setWebhookUrl(e.target.value)}
                      className="h-10 text-xs font-mono bg-muted/20 border-border" 
                    />
                    <Button 
                      size="sm" 
                      onClick={() => {
                        setIsSavingWebhook(true);
                        setTimeout(() => setIsSavingWebhook(false), 1000);
                      }}
                      className="h-10 px-4 text-xs font-bold bg-primary text-primary-foreground shadow-sm"
                    >
                      {isSavingWebhook ? <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                      Save URL
                    </Button>
                  </div>
                </div>

                {/* Webhook Signing Secret */}
                <div className="space-y-2 pt-2 border-t">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs font-bold text-foreground flex items-center gap-1.5">
                      <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
                      Webhook Signing Secret (`X-Paytaara-Signature`)
                    </Label>
                    <Badge variant="outline" className="text-[10px] font-bold uppercase text-emerald-600 border-emerald-500/20">HMAC SHA-512</Badge>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input 
                        type={showWebhookSecret ? "text" : "password"} 
                        readOnly 
                        value={keys.webhookSecret} 
                        className="font-mono text-xs h-10 bg-muted/30 pr-10 border-border" 
                      />
                      <button 
                        type="button" 
                        onClick={() => setShowWebhookSecret(!showWebhookSecret)} 
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showWebhookSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <Button variant="outline" size="sm" className="h-10 px-3 font-semibold text-xs" onClick={() => handleCopy(keys.webhookSecret, 'webhook')}>
                      {copiedKey === 'webhook' ? (
                        <>
                          <Check className="h-3.5 w-3.5 text-emerald-500 mr-1.5" /> Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Verify incoming headers using `crypto.createHmac('sha512', signingSecret)` to ensure requests originated from PayTaara.
                  </p>
                </div>

                {/* Event Selector Grid */}
                <div className="space-y-3 pt-2 border-t">
                  <Label className="text-xs font-bold text-foreground">Subscribe to Events</Label>
                  <div className="grid sm:grid-cols-2 gap-2.5">
                    {[
                      { name: "payment.success", desc: "Triggered on successful deposit / customer payment" },
                      { name: "payout.processed", desc: "Triggered when a disburse reaches beneficiary bank" },
                      { name: "virtual_account.created", desc: "Triggered when NUBAN is assigned" },
                      { name: "dispute.opened", desc: "Triggered on chargeback or refund request" }
                    ].map((evt) => {
                      const isChecked = selectedEvents.includes(evt.name);
                      return (
                        <div 
                          key={evt.name} 
                          onClick={() => handleToggleEvent(evt.name)}
                          className={cn(
                            "p-3 rounded-xl border transition-all cursor-pointer flex items-start gap-3",
                            isChecked ? "bg-primary/5 border-primary/30" : "bg-muted/20 border-border hover:bg-muted/40"
                          )}
                        >
                          <div className={cn(
                            "h-4 w-4 rounded border mt-0.5 flex items-center justify-center shrink-0 transition-colors",
                            isChecked ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40"
                          )}>
                            {isChecked && <Check className="h-3 w-3 stroke-[3]" />}
                          </div>
                          <div>
                            <p className="text-xs font-bold font-mono text-foreground">{evt.name}</p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">{evt.desc}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

              </CardContent>
            </Card>

            {/* Right: Simulation & Ping Test */}
            <Card className="border shadow-sm flex flex-col justify-between">
              <div>
                <CardHeader className="py-4 px-5 border-b bg-muted/10">
                  <div className="flex items-center gap-2">
                    <Play className="h-4 w-4 text-primary fill-current" />
                    <CardTitle className="text-sm font-bold">Webhook Event Simulator</CardTitle>
                  </div>
                  <CardDescription className="text-[11px]">
                    Send a test payload to your endpoint to verify receiver setup.
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="p-5 space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold text-foreground">Select Mock Event</Label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-xs font-semibold focus:ring-1 focus:ring-primary">
                      <option>payment.success (₦50,000.00)</option>
                      <option>payout.processed (Settled)</option>
                      <option>virtual_account.created (Wema NUBAN)</option>
                      <option>dispute.opened (Chargeback Notice)</option>
                    </select>
                  </div>

                  <div className="p-3 rounded-lg bg-zinc-950 text-zinc-100 font-mono text-[10px] overflow-x-auto space-y-1 border">
                    <p className="text-zinc-500">// Sample Payload Sent to Callback</p>
                    <p>{"{"}</p>
                    <p className="pl-3">"event": <span className="text-emerald-400">"payment.success"</span>,</p>
                    <p className="pl-3">"reference": <span className="text-emerald-400">"TRX-991823"</span>,</p>
                    <p className="pl-3">"amount": <span className="text-amber-400">5000000</span>,</p>
                    <p className="pl-3">"currency": <span className="text-emerald-400">"NGN"</span></p>
                    <p>{"}"}</p>
                  </div>
                </CardContent>
              </div>

              <div className="p-5 border-t bg-muted/10">
                <Button className="w-full bg-primary text-primary-foreground font-bold text-xs h-10 shadow-sm" onClick={() => alert("Test Webhook Payload Dispatched!")}>
                  <Play className="h-3.5 w-3.5 mr-2 fill-current" /> Send Test Webhook Ping
                </Button>
              </div>
            </Card>
          </div>

          {/* Webhook Delivery Table */}
          <Card className="border shadow-sm overflow-hidden">
            <CardHeader className="py-4 px-5 border-b bg-muted/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <CardTitle className="text-sm font-bold">Recent Webhook Logs</CardTitle>
                <CardDescription className="text-[11px]">Audit history of outgoing webhook deliveries.</CardDescription>
              </div>
              
              {/* Filter Controls */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 bg-background border border-border px-2 py-1 rounded-lg">
                  <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                  <select 
                    value={webhookFilter}
                    onChange={(e) => {
                      setWebhookFilter(e.target.value as any);
                      setWebhookPage(1);
                    }}
                    className="bg-transparent text-xs font-semibold focus:outline-none cursor-pointer"
                  >
                    <option value="all">All Statuses ({allWebhookLogs.length})</option>
                    <option value="success">Successful Only ({allWebhookLogs.filter(l => l.type === 'success').length})</option>
                    <option value="failed">Failed Only ({allWebhookLogs.filter(l => l.type === 'error').length})</option>
                  </select>
                </div>
                <Button variant="outline" size="sm" className="h-8 text-xs font-semibold">
                  <RefreshCw className="h-3 w-3 mr-1.5" /> Refresh Log
                </Button>
              </div>
            </CardHeader>

            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow>
                  <TableHead className="h-10 text-xs font-bold">Event Type</TableHead>
                  <TableHead className="h-10 text-xs font-bold">Response Code</TableHead>
                  <TableHead className="h-10 text-xs font-bold">Payload Summary</TableHead>
                  <TableHead className="h-10 text-xs font-bold">Attempts</TableHead>
                  <TableHead className="h-10 text-xs font-bold">Timestamp</TableHead>
                  <TableHead className="h-10 text-xs font-bold text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedWebhookLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-xs text-muted-foreground font-medium">
                      No webhook logs match the selected filter criteria.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedWebhookLogs.map((log) => (
                    <TableRow key={log.id} className="hover:bg-muted/10 transition-colors">
                      <TableCell className="font-mono text-xs font-bold text-foreground">{log.event}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn(
                          "text-[9px] font-bold uppercase border",
                          log.type === "success" ? "border-emerald-500/30 text-emerald-600 bg-emerald-500/10" : "border-destructive/30 text-destructive bg-destructive/10"
                        )}>
                          {log.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground truncate max-w-[220px]">{log.response}</TableCell>
                      <TableCell className="text-xs font-medium">{log.retries}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{log.time}</TableCell>
                      <TableCell className="text-right">
                        {log.type === "error" && (
                          <Button variant="outline" size="sm" className="h-7 text-[10px] font-bold text-destructive border-destructive/20 hover:bg-destructive/10">
                            Retry Delivery
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* Pagination Footer */}
            <div className="p-3.5 bg-muted/10 border-t flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
              <span className="text-muted-foreground font-medium">
                Showing <strong className="text-foreground font-semibold">{filteredWebhookLogs.length > 0 ? (webhookPage - 1) * ITEMS_PER_PAGE + 1 : 0}</strong> to{" "}
                <strong className="text-foreground font-semibold">{Math.min(webhookPage * ITEMS_PER_PAGE, filteredWebhookLogs.length)}</strong> of{" "}
                <strong className="text-foreground font-semibold">{filteredWebhookLogs.length}</strong> webhook logs
              </span>

              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={webhookPage === 1}
                  onClick={() => setWebhookPage(prev => Math.max(prev - 1, 1))}
                  className="h-8 px-3 text-xs font-semibold disabled:opacity-50"
                >
                  <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Previous
                </Button>

                <div className="flex items-center gap-1 text-xs font-bold px-2">
                  <span>Page {webhookPage} of {totalPages || 1}</span>
                </div>

                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={webhookPage >= totalPages || totalPages === 0}
                  onClick={() => setWebhookPage(prev => Math.min(prev + 1, totalPages))}
                  className="h-8 px-3 text-xs font-semibold disabled:opacity-50"
                >
                  Next <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* TAB 3: SDK & CODE PLAYGROUND */}
        <TabsContent value="code" className="space-y-6 m-0">
          <Card className="border shadow-sm overflow-hidden">
            <CardHeader className="py-4 px-5 border-b bg-muted/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <CardTitle className="text-sm font-bold flex items-center gap-2">
                  <FileCode2 className="h-4 w-4 text-primary" /> Integration SDK Code Samples
                </CardTitle>
                <CardDescription className="text-[11px]">
                  Copy-pasteable code examples pre-populated with your current environment credentials.
                </CardDescription>
              </div>

              {/* Language Tabs */}
              <div className="flex items-center gap-1 bg-muted p-1 rounded-lg border">
                {(["node", "php", "python", "curl"] as const).map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setActiveLang(lang)}
                    className={cn(
                      "px-3 py-1 text-xs font-bold rounded-md uppercase transition-all",
                      activeLang === lang ? "bg-background text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {lang === "node" ? "Node.js" : lang === "php" ? "PHP / Laravel" : lang === "python" ? "Python" : "cURL"}
                  </button>
                ))}
              </div>
            </CardHeader>

            <CardContent className="p-0 bg-zinc-950 text-zinc-100">
              <div className="p-4 border-b border-zinc-800 flex justify-between items-center text-xs font-mono text-zinc-400">
                <span>{activeLang === "node" ? "index.js" : activeLang === "php" ? "PaytaaraService.php" : activeLang === "python" ? "main.py" : "terminal.sh"}</span>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-zinc-300 hover:bg-zinc-800" onClick={() => handleCopy(codeSnippets[activeLang], 'code')}>
                  {copiedKey === 'code' ? <Check className="h-3.5 w-3.5 text-emerald-400 mr-1" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
                  Copy Code Snippet
                </Button>
              </div>
              <pre className="p-5 font-mono text-xs leading-relaxed overflow-x-auto text-emerald-300">
                <code>{codeSnippets[activeLang]}</code>
              </pre>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 4: REQUEST AUDIT LOGS */}
        <TabsContent value="logs" className="space-y-6 m-0">
          <Card className="border shadow-sm overflow-hidden">
            <CardHeader className="py-4 px-5 border-b bg-muted/10 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-sm font-bold">API Access Audit Trail</CardTitle>
                <CardDescription className="text-[11px]">Real-time stream of incoming REST API requests.</CardDescription>
              </div>
              <Button variant="outline" size="sm" className="h-8 text-xs font-semibold">
                Clear Filters
              </Button>
            </CardHeader>
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow>
                  <TableHead className="h-10 text-xs font-bold">Timestamp</TableHead>
                  <TableHead className="h-10 text-xs font-bold">Initiator / Key</TableHead>
                  <TableHead className="h-10 text-xs font-bold">Endpoint / Method</TableHead>
                  <TableHead className="h-10 text-xs font-bold">IP Address</TableHead>
                  <TableHead className="h-10 text-xs font-bold text-right">Result</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[
                  { time: "2026-10-07 06:40:01", actor: "isaac@paytaara.com", endpoint: "POST /v1/virtual-accounts", ip: "198.51.100.1", result: "201 Created" },
                  { time: "2026-10-07 06:30:15", actor: "System Webhook Worker", endpoint: "POST /v1/payouts/disburse", ip: "192.168.1.42", result: "200 OK" },
                  { time: "2026-10-07 05:12:33", actor: "isaac@paytaara.com", endpoint: "GET /v1/balances", ip: "198.51.100.1", result: "200 OK" },
                  { time: "2026-10-06 23:55:10", actor: "Unrecognized Key", endpoint: "POST /v1/payouts", ip: "104.22.3.1", result: "401 Unauthorized" }
                ].map((log, i) => (
                  <TableRow key={i} className="hover:bg-muted/10 transition-colors">
                    <TableCell className="text-xs font-medium text-muted-foreground">{log.time}</TableCell>
                    <TableCell className="text-xs font-bold text-foreground">{log.actor}</TableCell>
                    <TableCell className="font-mono text-xs text-primary font-semibold">{log.endpoint}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{log.ip}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className={cn(
                        "text-[9px] font-bold uppercase border",
                        log.result.includes("20") ? "border-emerald-500/30 text-emerald-600 bg-emerald-500/10" : "border-destructive/30 text-destructive bg-destructive/10"
                      )}>
                        {log.result}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
}

